
import { useState, Suspense, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { Button, Card, Input } from '@/components/ui';
import { BrandingLogoHeader } from '@/components/layout/BrandingLogoHeader';
import { api } from '@/lib/api';
import { AuthResponse } from '@/types';

function TrainingSignupForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { completeAuth, isAuthenticated, isLoading: authLoading } = useAuth();

  const [linkValid, setLinkValid] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLinkValid(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const response = await api.get<{ valid: boolean }>(
        `/auth/training-signup/validate?token=${encodeURIComponent(token)}`,
        { auth: false }
      );
      if (!cancelled) {
        setLinkValid(response.data?.valid === true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/prayer-partner', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (!token || linkValid === false) {
    return (
      <Card variant="elevated" padding="lg" className="text-center space-y-4">
        <p className="text-slate-700 dark:text-slate-300">
          This signup link is not available. It may be disabled or expired. Ask your trainer for a current link.
        </p>
        <Link to="/">
          <Button variant="outline">Back to home</Button>
        </Link>
      </Card>
    );
  }

  if (linkValid === null) {
    return (
      <Card variant="elevated" padding="lg" className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </Card>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await api.post<AuthResponse>(
        '/auth/training-signup',
        {
          token,
          email,
          password,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
        },
        { auth: false }
      );
      if (response.error) {
        setError(response.error);
        setIsSubmitting(false);
        return;
      }
      if (response.data) {
        completeAuth(response.data);
        navigate('/prayer-partner', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
      setIsSubmitting(false);
    }
  };

  return (
    <Card variant="elevated" padding="none" className="p-6 sm:p-8 space-y-5">
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="text-slate-600 dark:text-slate-400 text-sm">
          Create your prayer partner account. You will complete a recording consent form before responding with audio or video.
        </p>
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          disabled={isSubmitting}
          placeholder="you@example.com"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            disabled={isSubmitting}
          />
          <Input
            label="Last name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            disabled={isSubmitting}
          />
        </div>
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
          disabled={isSubmitting}
          placeholder="At least 8 characters"
        />
        <Input
          label="Confirm password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
          disabled={isSubmitting}
          placeholder="Repeat password"
        />
        <Button
          type="submit"
          fullWidth
          isLoading={isSubmitting}
          disabled={isSubmitting || !email || !password || !confirmPassword}
        >
          Create account
        </Button>
      </form>
    </Card>
  );
}

export default function TrainingSignupPage() {
  return (
    <>
      <meta name="robots" content="noindex" />
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <BrandingLogoHeader className="text-3xl" />
            <p className="text-slate-600 dark:text-slate-400 mt-2">
              Training signup
            </p>
          </div>
          <Suspense
            fallback={
              <Card variant="elevated" padding="lg" className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </Card>
            }
          >
            <TrainingSignupForm />
          </Suspense>
        </div>
      </main>
    </>
  );
}
